// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { CustomAppState, xCustomProductDetailsState } from "@hardwoods/App/Store/Reducers";
import { getLineNotes, getProductAdditionalInfos } from "@hardwoods/App/Store/Selectors/CustomProductDetails";
import CartIcon from "@hardwoods/XDL/Icons/Cart";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import buttonSizeVariants from "@insite/mobius/Button/buttonSizeVariants";
import Check from "@insite/mobius/Icons/Check";
import { Icon, LoadingSpinner } from "@insite/mobius/index";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import get from "@insite/mobius/utilities/get";
import React, { NamedExoticComponent, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    iconOnly?: boolean;
    isProductDetailsPage?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: CustomAppState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);
    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    return {
        productInfo: props.productContext.productInfo,
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        lineNotes: getLineNotes(state),
        productAdditionalInfos: getProductAdditionalInfos(state),
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {
    css: css`
        padding: ${(props: any) => get(buttonSizeVariants, [props._sizeVariant, "padding"]) || "0 1em"};
        display: flex;
        justify-content: center;
        align-items: center;
        &.button-wrapper-icon-only {
            height: 38px;
            width: 65px;
            padding: 4px 0;
            display: flex;
            justify-content: center;
            align-items: center;
        }
    `,
};

interface AddToCartStatusInfoInterface {
    label: string;
    icon: NamedExoticComponent<{}> | string;
}

interface AddToCartStatusInterface {
    default: AddToCartStatusInfoInterface;
    loading: AddToCartStatusInfoInterface;
    success: AddToCartStatusInfoInterface;
}

const addToCartStatus: AddToCartStatusInterface = {
    default: {
        label: "Add to Cart",
        icon: CartIcon,
    },
    loading: {
        label: "Adding",
        icon: "",
    },
    success: {
        label: "Success",
        icon: Check,
    },
};

const ProductAddToCartButton = ({
    productContext: {
        product: {
            id: productId,
            allowZeroPricing,
            quoteRequired,
            properties: { itemSpecIsRequired, styledChildrenCount, styledChildrenId },
        },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory },
    },
    productInfo,
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    iconOnly,
    isProductDetailsPage,
    lineNotes,
    productAdditionalInfos,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeWithCss(productAddToCartButtonStyles, extendedStyles));
    const [status, setStatus] = useState(addToCartStatus.default);

    useEffect(() => {
        if (status === addToCartStatus.success) {
            setTimeout(() => {
                setStatus(addToCartStatus.default);
            }, 800);
        }
    }, [status]);

    if ((!hasEnoughInventory && !failedToLoadInventory) || !(canAddToCart || styledChildrenCount === "1")) {
        return null;
    }

    const dispatch = useDispatch();

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();

        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        if (isProductDetailsPage) {
            if (productAdditionalInfos.itemSpecIsRequired === "True" && !lineNotes) {
                dispatch({
                    type: "Data/CustomProductDetails/LineNotesHasError",
                    lineNotesHasError: true,
                });
                return;
            }
            dispatch({
                type: "Data/CustomProductDetails/LineNotesHasError",
                lineNotesHasError: false,
            });
            notes = lineNotes;
        }
        setStatus(addToCartStatus.loading);
        const cartline = (await addToCart({
            productId: styledChildrenId || productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        setStatus(addToCartStatus.success);

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
            });
        }

        dispatch({
            type: "Data/CustomProductDetails/ClearLineNotes",
        });
    };

    const isDisabled =
        qtyOrdered <= 0 ||
        status !== addToCartStatus.default ||
        isAddToCartButtonDisabled ||
        pricing?.unitNetPrice === 0 ||
        productInfo.failedToLoadPricing ||
        productInfo.failedToLoadInventory;

    if (iconOnly) {
        return (
            <Button
                {...styles}
                onClick={addToCartClickHandler}
                className="button-wrapper-icon-only"
                disabled={isDisabled}
                {...otherProps}
            >
                {status === addToCartStatus.loading ? (
                    <LoadingSpinner size={18} color="text.main" />
                ) : (
                    <Icon src={status.icon} size={22} color="common.white" />
                )}
            </Button>
        );
    }

    const icon = status.icon === addToCartStatus.default.icon ? "" : status.icon;
    return (
        <Button
            icon={{ src: icon, position: "left" }}
            {...styles}
            onClick={addToCartClickHandler}
            disabled={isDisabled}
            {...otherProps}
        >
            {status === addToCartStatus.loading ? (
                <LoadingSpinner
                    size={15}
                    color="text.main"
                    css={css`
                        margin-right: 8px;
                        margin-top: -1px;
                    `}
                />
            ) : null}
            {labelOverride ?? translate(status.label ?? "")}
        </Button>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
